import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Upload, Loader2, Trash2, Check, Images } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Persistent reference-image library for AI image generation.
 * Admin uploads style samples / brand characters once; per generation
 * they tick which ones to attach (max 4). Selected ids are passed as
 * `reference_image_ids` to the cover-image / social-image endpoints.
 */
export const RefImageLibrary = ({ selected = [], onChange }) => {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await apiFetch('/api/admin/ref-images');
    if (data?.images) setImages(data.images);
    setLoaded(true);
  };

  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const form = new FormData();
    Array.from(files).slice(0, 10).forEach((f) => form.append('files', f));
    try {
      const res = await fetch(`${API}/api/admin/ref-images`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Upload failed');
      toast.success(`Uploaded ${data.images.length} reference image(s)`);
      await load();
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async (id) => {
    const { error } = await apiFetch(`/api/admin/ref-images/${id}`, { method: 'DELETE' });
    if (error) return toast.error(error);
    onChange?.(selected.filter((s) => s !== id));
    setImages((prev) => prev.filter((i) => i.id !== id));
    toast.success('Reference image deleted');
  };

  const toggle = (id) => {
    if (selected.includes(id)) {
      onChange?.(selected.filter((s) => s !== id));
    } else {
      if (selected.length >= 4) return toast.error('Max 4 reference images per generation');
      onChange?.([...selected, id]);
    }
  };

  return (
    <div data-testid="ref-image-library">
      <div className="flex items-center justify-between">
        <Label className="text-xs flex items-center gap-1.5">
          <Images className="h-3.5 w-3.5" />
          Reference images
          <span className="text-muted-foreground font-normal">
            (optional — pick up to 4; the AI copies their style/characters)
          </span>
        </Label>
        <Button
          type="button" size="sm" variant="ghost" className="h-7 text-xs"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="ref-image-upload-btn"
        >
          {uploading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
          Upload
        </Button>
        <input
          ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
          multiple className="hidden"
          onChange={(e) => upload(e.target.files)}
          data-testid="ref-image-file-input"
        />
      </div>
      {loaded && images.length === 0 ? (
        <p className="text-xs text-muted-foreground mt-1">
          No reference images yet — upload style samples or brand characters once, reuse them for every generation.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2 mt-2">
          {images.map((img) => {
            const isSel = selected.includes(img.id);
            return (
              <div key={img.id} className="relative group">
                <button
                  type="button"
                  onClick={() => toggle(img.id)}
                  className={`block rounded-md overflow-hidden border-2 transition ${
                    isSel ? 'border-[#00A896] ring-2 ring-[#00E5CC]/40' : 'border-transparent hover:border-muted-foreground/40'
                  }`}
                  title={img.name}
                  data-testid={`ref-image-tile-${img.id}`}
                >
                  <img src={`${API}${img.url}`} alt={img.name} className="w-16 h-16 object-cover" loading="lazy" />
                  {isSel && (
                    <span className="absolute top-1 left-1 bg-[#00A896] text-white rounded-full p-0.5">
                      <Check className="h-3 w-3" />
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => remove(img.id)}
                  className="absolute -top-1.5 -right-1.5 hidden group-hover:flex bg-red-500 text-white rounded-full p-0.5"
                  title="Delete from library"
                  data-testid={`ref-image-delete-${img.id}`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RefImageLibrary;
